import React, {
  useEffect,
  useState,
  ChangeEvent,
} from "react";

import axios from "axios";

import API from "../../../services/api";
import Navbar from "../../../components/Navbar";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";

import Modal from "./Modal";
import List from "./List";

import "./index.scss";

/* =========================================================
   INTERFACES
========================================================= */

export interface ProductCategory {
  _id: string;
  name: string;
  image?: string;
}

export interface Product {
  _id: string;
  name: string;
  quantity: number;

  price: string | number;
  mrp_price?: string | number;
  bommenhali_price?: string | number;
  kr_price?: string | number;

  description: string;

  image?: string;
  images?: string[];

  category?: ProductCategory | null;
  sub_category?: ProductCategory | null;

  order_closing_time?: string | null;

  isSubcibed?: boolean;
  quantitySubcribed?: number;

  type?: any;

  quantity_added?: number;

  item_id?: string | null;
  delivery_id?: string | null;

  is_tomorrow?: boolean;
  is_order_placed?: boolean;

  can_update?: boolean;

  order_id?: string | null;

  /* NEW */
  status?: "active" | "inactive";
}

export interface Category {
  _id: string;
  name: string;

  description?: string;
  image?: string;

  parent_id?: string | null;

  status?: string;

  level?: number;
  path?: string;

  slug?: string;

  has_subcategory?: boolean;
}

export interface ProductForm {
  name: string;

  quantity: string;

  price: string;

  mrp_price?: string;

  bommenhali_price?: string;

  kr_price?: string;

  description: string;

  image: File | null;

  images: File[];

  category_id: string;

  sub_category_id: string;

  order_closing_time: string;

  isSubcibed: boolean;

  quantitySubcribed: string;

  /* NEW */
  status: "active" | "inactive";
}

export type FormErrors = Partial<
  Record<keyof ProductForm, string>
>;

/* =========================================================
   INITIAL FORM
========================================================= */

const initialForm: ProductForm = {
  name: "",

  quantity: "",

  price: "",

  mrp_price: "",

  bommenhali_price: "",

  kr_price: "",

  description: "",

  image: null,

  images: [],

  category_id: "",

  sub_category_id: "",

  order_closing_time: "19:00",

  isSubcibed: false,

  quantitySubcribed: "",

  /* NEW - Active by default */
  status: "active",
};

const PRODUCTS_PER_PAGE = 20;
const QUANTITY_FILTER_OPTIONS = [1, 2, 3, 4, 5, 6, 10, 12, 20, 50, 100, 500, 1000];

/* =========================================================
   COMPONENT
========================================================= */

export default function Products() {
  const [products, setProducts] =
    useState<Product[]>([]);

  const [
    loadingProducts,
    setLoadingProducts,
  ] = useState(false);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const [
    hasNextPage,
    setHasNextPage,
  ] = useState(false);

  /* =========================================================
     FILTERS
  ========================================================= */

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("");

  const [
    selectedSubCategory,
    setSelectedSubCategory,
  ] = useState("");

  const [quantitySort, setQuantitySort] = useState<
    "default" | "low-to-high" | "high-to-low"
  >("default");

  const [quantityFilter, setQuantityFilter] = useState("");
  const [mrpFilter, setMrpFilter] = useState<"" | "zero">("");

  /* =========================================================
     CATEGORIES
  ========================================================= */

  const [
    categories,
    setCategories,
  ] = useState<Category[]>([]);

  const [
    subCategories,
    setSubCategories,
  ] = useState<Category[]>([]);

  const [
    loadingCategories,
    setLoadingCategories,
  ] = useState(false);

  const [
    loadingSubCategories,
    setLoadingSubCategories,
  ] = useState(false);

  /* =========================================================
     MODALS
  ========================================================= */

  const [
    showModal,
    setShowModal,
  ] = useState(false);

  const [
    showEditModal,
    setShowEditModal,
  ] = useState(false);

  const [
    editId,
    setEditId,
  ] = useState<string | null>(
    null
  );

  const [
    showDeleteModal,
    setShowDeleteModal,
  ] = useState(false);

  const [
    deleteId,
    setDeleteId,
  ] = useState<string | null>(
    null
  );

  /* =========================================================
     FORMS
  ========================================================= */

  const [
    form,
    setForm,
  ] = useState<ProductForm>({
    ...initialForm,
  });

  const [
    editForm,
    setEditForm,
  ] = useState<ProductForm>({
    ...initialForm,
  });

  /* =========================================================
     PREVIEW
  ========================================================= */

  const [
    preview,
    setPreview,
  ] = useState<string | null>(
    null
  );

  /* =========================================================
     ERRORS
  ========================================================= */

  const [
    errors,
    setErrors,
  ] = useState<FormErrors>({});

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  /* =========================================================
     FETCH PRODUCTS
  ========================================================= */

  const fetchProducts = async (
    page: number = 1,
    categoryId: string =
      selectedCategory,
    subCategoryId: string =
      selectedSubCategory
  ) => {
    try {
      setLoadingProducts(true);

      const response =
        await API.get(
          "/products",
          {
            params: {
              page,

              limit:
                PRODUCTS_PER_PAGE,

              ...(categoryId
                ? {
                    category_id:
                      categoryId,
                  }
                : {}),

              ...(subCategoryId
                ? {
                    sub_category_id:
                      subCategoryId,
                  }
                : {}),
            },
          }
        );

      console.log(
        "PRODUCT RESPONSE:",
        response.data
      );

      const responseData =
        response.data?.data;

      let productArray: Product[] =
        [];

      if (
        Array.isArray(
          responseData
        )
      ) {
        productArray =
          responseData;
      } else if (
        Array.isArray(
          responseData?.products
        )
      ) {
        productArray =
          responseData.products;
      }

      if (
        page > 1 &&
        productArray.length === 0
      ) {
        setHasNextPage(false);

        return;
      }

      const normalizedProducts =
        productArray.map(
          (item) => ({
            ...item,

            price: String(
              item.price ?? ""
            ),

            mrp_price: String(
              item.mrp_price ?? ""
            ),

            bommenhali_price:
              String(
                item.bommenhali_price ??
                  ""
              ),

            kr_price: String(
              item.kr_price ?? ""
            ),

            /* NEW */
            status:
              item.status ||
              "active",
          })
        );

      setProducts(
        normalizedProducts
      );

      setCurrentPage(page);

      setHasNextPage(
        productArray.length ===
          PRODUCTS_PER_PAGE
      );
    } catch (error) {
      console.error(
        "PRODUCT ERROR:",
        error
      );

      if (
        axios.isAxiosError(
          error
        )
      ) {
        console.log(
          error.response?.data
        );
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleMrpFilterChange = async (
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value as "" | "zero";

    setMrpFilter(value);

    if (value === "zero") {
      setQuantityFilter("");
      setQuantitySort("default");
      setSelectedCategory("");
      setSelectedSubCategory("");
      setSubCategories([]);

      await fetchProducts(1, "", "");
    }
  };

  /* =========================================================
     CATEGORY FILTER
  ========================================================= */

  const handleCategoryFilter =
    async (
      e: ChangeEvent<HTMLSelectElement>
    ) => {
      const categoryId =
        e.target.value;

      setSelectedCategory(
        categoryId
      );

      setSelectedSubCategory(
        ""
      );

      setCurrentPage(1);

      if (categoryId) {
        await fetchSubCategories(
          categoryId
        );
      } else {
        setSubCategories([]);
      }

      await fetchProducts(
        1,
        categoryId,
        ""
      );
    };

  /* =========================================================
     SUBCATEGORY FILTER
  ========================================================= */

  const handleSubCategoryFilter =
    async (
      e: ChangeEvent<HTMLSelectElement>
    ) => {
      const subCategoryId =
        e.target.value;

      setSelectedSubCategory(
        subCategoryId
      );

      setCurrentPage(1);

      await fetchProducts(
        1,
        selectedCategory,
        subCategoryId
      );
    };

  /* =========================================================
     NEXT PAGE
  ========================================================= */

  const handleNextPage =
    async () => {
      if (
        loadingProducts ||
        !hasNextPage
      ) {
        return;
      }

      await fetchProducts(
        currentPage + 1,
        selectedCategory,
        selectedSubCategory
      );
    };

  /* =========================================================
     PREVIOUS PAGE
  ========================================================= */

  const handlePreviousPage =
    async () => {
      if (
        loadingProducts ||
        currentPage <= 1
      ) {
        return;
      }

      await fetchProducts(
        currentPage - 1,
        selectedCategory,
        selectedSubCategory
      );
    };

  /* =========================================================
     FETCH CATEGORIES
  ========================================================= */

  const fetchCategories =
    async () => {
      try {
        setLoadingCategories(
          true
        );

        const response =
          await API.get(
            "/categories"
          );

        console.log(
          "CATEGORY RESPONSE:",
          response.data
        );

        const categoryArray:
          Category[] =
          response.data?.data
            ?.categories || [];

        const parentCategories =
          categoryArray.filter(
            (category) =>
              category.parent_id ===
              null
          );

        setCategories(
          parentCategories
        );
      } catch (error) {
        console.error(
          "CATEGORY ERROR:",
          error
        );

        setCategories([]);
      } finally {
        setLoadingCategories(
          false
        );
      }
    };

  /* =========================================================
     FETCH SUBCATEGORIES
  ========================================================= */

  const fetchSubCategories =
    async (
      categoryId: string
    ) => {
      if (!categoryId) {
        setSubCategories([]);

        return;
      }

      try {
        setLoadingSubCategories(
          true
        );

        const response =
          await API.get(
            `/categories/${categoryId}/subcategories`
          );

        console.log(
          "SUBCATEGORY RESPONSE:",
          response.data
        );

        const responseData =
          response.data?.data;

        let result: Category[] =
          [];

        if (
          Array.isArray(
            responseData
          )
        ) {
          result =
            responseData;
        } else if (
          Array.isArray(
            responseData
              ?.subcategories
          )
        ) {
          result =
            responseData
              .subcategories;
        }

        setSubCategories(
          result
        );
      } catch (error) {
        console.error(
          "SUBCATEGORY ERROR:",
          error
        );

        setSubCategories([]);
      } finally {
        setLoadingSubCategories(
          false
        );
      }
    };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    fetchProducts(
      1,
      "",
      ""
    );

    fetchCategories();
  }, []);

  /* =========================================================
     FORM CHANGE
  ========================================================= */

  const handleChange = (
    e: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
    >,
    isEdit = false
  ) => {
    const {
      name,
      value,
      type,
    } = e.target;

    const checked =
      type === "checkbox"
        ? (
            e.target as HTMLInputElement
          ).checked
        : false;

    /* =====================================================
       CATEGORY CHANGE
    ===================================================== */

    if (
      name ===
      "category_id"
    ) {
      if (isEdit) {
        setEditForm(
          (previous) => ({
            ...previous,

            category_id:
              value,

            sub_category_id:
              "",
          })
        );
      } else {
        setForm(
          (previous) => ({
            ...previous,

            category_id:
              value,

            sub_category_id:
              "",
          })
        );
      }

      setSubCategories([]);

      if (value) {
        fetchSubCategories(
          value
        );
      }

      setErrors(
        (previous) => ({
          ...previous,

          category_id: "",
        })
      );

      return;
    }

    /* =====================================================
       OTHER INPUTS
    ===================================================== */

    if (isEdit) {
      setEditForm(
        (previous) => ({
          ...previous,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        })
      );
    } else {
      setForm(
        (previous) => ({
          ...previous,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        })
      );
    }

    setErrors(
      (previous) => ({
        ...previous,

        [name]: "",
      })
    );
  };

  /* =========================================================
     IMAGE
  ========================================================= */

  const handleImageChange = (
    e: ChangeEvent<HTMLInputElement>,
    isEdit = false
  ) => {
    const file =
      e.target.files?.[0];

    if (!file) {
      return;
    }

    if (isEdit) {
      setEditForm(
        (previous) => ({
          ...previous,

          image: file,
        })
      );
    } else {
      setForm(
        (previous) => ({
          ...previous,

          image: file,
        })
      );
    }

    setPreview(
      URL.createObjectURL(
        file
      )
    );
  };

  const handleImagesChange = (
    e: ChangeEvent<HTMLInputElement>,
    isEdit = false
  ) => {
    const files = Array.from(e.target.files || []);

    if (isEdit) {
      setEditForm((previous) => ({
        ...previous,
        images: files,
      }));
    } else {
      setForm((previous) => ({
        ...previous,
        images: files,
      }));
    }
  };

  /* =========================================================
     VALIDATE
  ========================================================= */

  const validate = (
    data: ProductForm
  ) => {
    const validationErrors:
      FormErrors = {};

    if (!data.name.trim()) {
      validationErrors.name =
        "Name is required";
    }

    if (!data.quantity) {
      validationErrors.quantity =
        "Quantity is required";
    }

    if (!data.price) {
      validationErrors.price =
        "Price is required";
    }

    if (!data.mrp_price) {
      validationErrors.mrp_price =
        "MRP price is required";
    }

    if (
      !data.description.trim()
    ) {
      validationErrors.description =
        "Description is required";
    }

    if (!data.category_id) {
      validationErrors.category_id =
        "Category is required";
    }

    setErrors(
      validationErrors
    );

    return (
      Object.keys(
        validationErrors
      ).length === 0
    );
  };

  /* =========================================================
     CREATE FORM DATA
  ========================================================= */

  const createFormData = (
    data: ProductForm
  ) => {
    const formData =
      new FormData();

    formData.append(
      "name",
      data.name.trim()
    );

    formData.append(
      "price",
      data.price
    );

    formData.append(
      "mrp_price",
      data.mrp_price ?? ""
    );

    formData.append(
      "bommenhali_price",
      data.bommenhali_price ??
        ""
    );

    formData.append(
      "kr_price",
      data.kr_price ?? ""
    );

    formData.append(
      "quantity",
      data.quantity
    );

    formData.append(
      "description",
      data.description.trim()
    );

    formData.append(
      "category_id",
      data.category_id
    );

    if (
      data.sub_category_id
    ) {
      formData.append(
        "sub_category_id",
        data.sub_category_id
      );
    } else {
      /*
       * Important while editing:
       * send empty value if subcategory
       * has been removed.
       */
      formData.append(
        "sub_category_id",
        ""
      );
    }

    if (
      data.order_closing_time
    ) {
      formData.append(
        "order_closing_time",
        data.order_closing_time
      );
    }

    if (
      data.image instanceof
      File
    ) {
      formData.append(
        "image",
        data.image
      );
    }

    data.images.forEach((file) => {
      formData.append("images", file);
    });

    formData.append(
      "isSubcibed",
      String(
        data.isSubcibed
      )
    );

    if (
      data.quantitySubcribed
    ) {
      formData.append(
        "quantitySubcribed",
        data.quantitySubcribed
      );
    }

    /* =====================================================
       ACTIVE / INACTIVE
    ===================================================== */

    formData.append(
      "status",
      data.status ||
        "active"
    );

    return formData;
  };

  /* =========================================================
     ADD PRODUCT
  ========================================================= */

  const handleSubmit =
    async () => {
      if (!validate(form)) {
        return;
      }

      try {
        setSubmitting(true);

        const formData =
          createFormData(
            form
          );

        await API.post(
          "/add-product",
          formData
        );

        setShowModal(false);

        setForm({
          ...initialForm,
        });

        setPreview(null);

        setSubCategories([]);

        await fetchProducts(
          1,
          selectedCategory,
          selectedSubCategory
        );
      } catch (error) {
        console.error(
          "ADD ERROR:",
          error
        );

        if (
          axios.isAxiosError(
            error
          )
        ) {
          alert(
            error.response
              ?.data?.message ||
              "Failed to add product"
          );
        }
      } finally {
        setSubmitting(false);
      }
    };

  /* =========================================================
     OPEN EDIT
  ========================================================= */

  const handleUpdate =
    async (
      product: Product
    ) => {
      console.log(
        "EDIT:",
        product
      );

      const categoryId =
        product.category
          ?._id || "";

      const subCategoryId =
        product.sub_category
          ?._id || "";

      setEditId(
        product._id
      );

      if (categoryId) {
        await fetchSubCategories(
          categoryId
        );
      } else {
        setSubCategories([]);
      }

      setEditForm({
        name:
          product.name || "",

        quantity:
          String(
            product.quantity ??
              ""
          ),

        price:
          String(
            product.price ?? ""
          ),

        mrp_price:
          String(
            product.mrp_price ?? ""
          ),

        bommenhali_price:
          String(
            product.bommenhali_price ??
              ""
          ),

        kr_price:
          String(
            product.kr_price ??
              ""
          ),

        description:
          product.description ||
          "",

        image: null,

        images: [],

        category_id:
          categoryId,

        sub_category_id:
          subCategoryId,

        order_closing_time:
          product.order_closing_time ||
          "19:00",

        isSubcibed:
          Boolean(
            product.isSubcibed
          ),

        quantitySubcribed:
          product.quantitySubcribed
            ? String(
                product.quantitySubcribed
              )
            : "",

        /* NEW */
        status:
          product.status ||
          "active",
      });

      setPreview(
        product.image || null
      );

      setErrors({});

      setShowEditModal(true);
    };

  /* =========================================================
     UPDATE PRODUCT
  ========================================================= */

  const handleEditSubmit =
    async () => {
      if (!editId) {
        return;
      }

      if (
        !validate(
          editForm
        )
      ) {
        return;
      }

      try {
        setSubmitting(true);

        const formData =
          createFormData(
            editForm
          );

        await API.put(
          `/product/${editId}`,
          formData
        );

        setShowEditModal(
          false
        );

        setEditId(null);

        setEditForm({
          ...initialForm,
        });

        setPreview(null);

        setSubCategories([]);

        await fetchProducts(
          currentPage,
          selectedCategory,
          selectedSubCategory
        );
      } catch (error) {
        console.error(
          "UPDATE ERROR:",
          error
        );

        if (
          axios.isAxiosError(
            error
          )
        ) {
          alert(
            error.response
              ?.data?.message ||
              "Failed to update product"
          );
        }
      } finally {
        setSubmitting(false);
      }
    };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = (
    id: string
  ) => {
    setDeleteId(id);

    setShowDeleteModal(
      true
    );
  };

  const confirmDelete =
    async () => {
      if (!deleteId) {
        return;
      }

      try {
        await API.delete(
          `/product/${deleteId}`
        );

        setShowDeleteModal(
          false
        );

        setDeleteId(null);

        if (
          products.length ===
            1 &&
          currentPage > 1
        ) {
          await fetchProducts(
            currentPage - 1,
            selectedCategory,
            selectedSubCategory
          );
        } else {
          await fetchProducts(
            currentPage,
            selectedCategory,
            selectedSubCategory
          );
        }
      } catch (error) {
        console.error(
          "DELETE ERROR:",
          error
        );
      }
    };

  /* =========================================================
     MODAL CONTROLS
  ========================================================= */

  const openAddModal =
    () => {
      setForm({
        ...initialForm,

        /* New products active */
        status: "active",
      });

      setPreview(null);

      setErrors({});

      setSubCategories([]);

      setShowModal(true);
    };

  const closeAddModal =
    () => {
      setShowModal(false);

      setForm({
        ...initialForm,
      });

      setPreview(null);

      setSubCategories([]);

      setErrors({});
    };

  const closeEditModal =
    () => {
      setShowEditModal(
        false
      );

      setEditId(null);

      setEditForm({
        ...initialForm,
      });

      setPreview(null);

      setSubCategories([]);

      setErrors({});
    };

  const hasZeroOrMissingMrp = (product: Product) => {
    const mrpValue = product.mrp_price;

    if (
      mrpValue === undefined ||
      mrpValue === null ||
      String(mrpValue).trim() === ""
    ) {
      return true;
    }

    return Number(mrpValue) === 0;
  };

  const zeroMrpProducts = products.filter(hasZeroOrMissingMrp);

  const quantityFilteredProducts = products.filter((product) =>
    quantityFilter === ""
      ? true
      : Number(product.quantity) === Number(quantityFilter)
  );

  const productsToDisplay =
    mrpFilter === "zero"
      ? zeroMrpProducts
      : quantityFilteredProducts;

  const displayedProducts = [...productsToDisplay].sort(
    (first, second) => {
      if (quantitySort === "default") {
        return 0;
      }

      const firstQuantity = Number(first.quantity) || 0;
      const secondQuantity = Number(second.quantity) || 0;

      return quantitySort === "low-to-high"
        ? firstQuantity - secondQuantity
        : secondQuantity - firstQuantity;
    }
  );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <>
      <Navbar />

      <div className="products-page">
        <div className="products-header my-2">
          {/* CATEGORY FILTER */}

          <select
            className="category-filter"
            value={
              selectedCategory
            }
            onChange={
              handleCategoryFilter
            }
          >
            <option value="">
              All Categories
            </option>

            {categories.map(
              (category) => (
                <option
                  key={
                    category._id
                  }
                  value={
                    category._id
                  }
                >
                  {category.name}
                </option>
              )
            )}
          </select>

          {/* SUBCATEGORY FILTER */}

          <select
            className="category-filter"
            value={
              selectedSubCategory
            }
            onChange={
              handleSubCategoryFilter
            }
            disabled={
              !selectedCategory ||
              loadingSubCategories
            }
          >
            <option value="">
              {loadingSubCategories
                ? "Loading subcategories..."
                : selectedCategory
                ? "All Subcategories"
                : "Select Category First"}
            </option>

            {subCategories.map(
              (
                subCategory
              ) => (
                <option
                  key={
                    subCategory._id
                  }
                  value={
                    subCategory._id
                  }
                >
                  {
                    subCategory.name
                  }
                </option>
              )
            )}
          </select>

          {/* MRP FILTER */}

          <select
            className="category-filter"
            value={mrpFilter}
            onChange={handleMrpFilterChange}
            aria-label="Filter products by MRP"
          >
            <option value="">All MRP Values</option>
            <option value="zero">
              MRP: 0 / Missing ({zeroMrpProducts.length})
            </option>
          </select>

          {/* EXACT QUANTITY FILTER */}

          <select
            className="category-filter"
            value={quantityFilter}
            onChange={(event) => setQuantityFilter(event.target.value)}
            aria-label="Filter products by exact quantity"
          >
            <option value="">All Quantities</option>
            {QUANTITY_FILTER_OPTIONS.map((quantity) => (
              <option key={quantity} value={quantity}>
                Quantity: {quantity}
              </option>
            ))}
          </select>

          {/* QUANTITY SORT */}

          <select
            className="category-filter"
            value={quantitySort}
            onChange={(event) =>
              setQuantitySort(
                event.target.value as
                  | "default"
                  | "low-to-high"
                  | "high-to-low"
              )
            }
            aria-label="Sort products by quantity"
          >
            <option value="default">Sort by Quantity</option>
            <option value="low-to-high">Quantity: Low to High</option>
            <option value="high-to-low">Quantity: High to Low</option>
          </select>

          {/* ADD PRODUCT */}

          <button
            type="button"
            className="btn primary add-product px-2 py-2"
            onClick={
              openAddModal
            }
          >
            + Add Product
          </button>
        </div>

        {/* PRODUCTS */}

        {loadingProducts ? (
          <div className="loading">
            Loading products...
          </div>
        ) : displayedProducts.length ===
          0 ? (
          <div className="loading">
            {mrpFilter === "zero"
              ? "No products found with MRP 0 or missing"
              : quantityFilter
              ? `No products found with quantity ${quantityFilter}`
              : "No products found"}
          </div>
        ) : (
          <>
            <List
              products={
                displayedProducts
              }
              handleUpdate={
                handleUpdate
              }
              handleDelete={
                handleDelete
              }
            />

            {/* PAGINATION */}

            <div className="products-pagination">
              <button
                type="button"
                className="pagination-button"
                disabled={
                  currentPage ===
                    1 ||
                  loadingProducts
                }
                onClick={
                  handlePreviousPage
                }
              >
                ← Back
              </button>

              <div className="pagination-current">
                Page{" "}
                {currentPage}
              </div>

              <button
                type="button"
                className="pagination-button active"
                disabled={
                  !hasNextPage ||
                  loadingProducts
                }
                onClick={
                  handleNextPage
                }
              >
                Next →
              </button>
            </div>

            {/* FILTER INFO */}

            <div className="pagination-info">
              Showing{" "}
              {displayedProducts.length}{" "}
              of{" "}
              {products.length}{" "}
              products

              {selectedCategory && (
                <>
                  {" "}in{" "}

                  <strong>
                    {
                      categories.find(
                        (
                          category
                        ) =>
                          category._id ===
                          selectedCategory
                      )?.name
                    }
                  </strong>
                </>
              )}

              {selectedSubCategory && (
                <>
                  {" / "}

                  <strong>
                    {
                      subCategories.find(
                        (
                          subCategory
                        ) =>
                          subCategory._id ===
                          selectedSubCategory
                      )?.name
                    }
                  </strong>
                </>
              )}
            </div>
          </>
        )}

        {/* ADD MODAL */}

        {showModal && (
          <Modal
            title="Add Product"

            submitLabel={
              submitting
                ? "Adding..."
                : "Add Product"
            }

            setShowModal={
              closeAddModal
            }

            form={form}

            handleChange={(
              e
            ) =>
              handleChange(
                e,
                false
              )
            }

            handleImageChange={(
              e
            ) =>
              handleImageChange(
                e,
                false
              )
            }

            handleImagesChange={(
              e
            ) =>
              handleImagesChange(
                e,
                false
              )
            }

            handleSubmit={
              handleSubmit
            }

            errors={errors}

            preview={preview}

            categories={
              categories
            }

            subCategories={
              subCategories
            }

            loadingCategories={
              loadingCategories
            }

            loadingSubCategories={
              loadingSubCategories
            }
          />
        )}

        {/* EDIT MODAL */}

        {showEditModal && (
          <Modal
            title="Edit Product"

            submitLabel={
              submitting
                ? "Updating..."
                : "Update Product"
            }

            setShowModal={
              closeEditModal
            }

            form={
              editForm
            }

            handleChange={(
              e
            ) =>
              handleChange(
                e,
                true
              )
            }

            handleImageChange={(
              e
            ) =>
              handleImageChange(
                e,
                true
              )
            }

            handleImagesChange={(
              e
            ) =>
              handleImagesChange(
                e,
                true
              )
            }

            handleSubmit={
              handleEditSubmit
            }

            errors={errors}

            preview={preview}

            categories={
              categories
            }

            subCategories={
              subCategories
            }

            loadingCategories={
              loadingCategories
            }

            loadingSubCategories={
              loadingSubCategories
            }
          />
        )}

        {/* DELETE MODAL */}

        <DeleteConfirmationModal
          isOpen={
            showDeleteModal
          }

          title="Delete Product"

          message="Are you sure you want to delete this product? This action cannot be undone."

          onClose={() => {
            setShowDeleteModal(
              false
            );

            setDeleteId(
              null
            );
          }}

          onConfirm={
            confirmDelete
          }
        />
      </div>
    </>
  );
}
